/**
 * Cattle: find animals fast, see groups and lots, act on a selection. Current, former and reference
 * stay distinct. The list loads a page at a time as you scroll, so a 2,000-head ranch stays quick,
 * and the filters live in the address so Back and shared links keep them.
 */
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ANIMAL_CLASS, type AnimalSummary, type GroupSummary } from "../../../contract";
import { api, qs } from "../../lib/api";
import { formatAge } from "../../lib/format";
import { plural, t, useLanguage } from "../../lib/i18n";
import { useApi } from "../../lib/query";
import { useCan } from "../../lib/session";
import { openEntity, openRecord } from "../../forms/EventForm";
import { entitySections, eventTypesFor } from "../../registry";
import { Spinner, Badges, Button, Card, Checkbox, Chip, Empty, ErrorNote, Icon, IconButton, List, Loading, Menu, PageHeader, Row, Section, Select, Tabs, TagLabel, TextInput, cx, toast, useFromHere, useLocalState } from "../../ui/kit";
import { cattleReportLink } from "../../../modules/reports/web/link";
import { useCaptureSubject, useOpenComposer } from "../Composer";
import { classLabel } from "../describe";
import { Page } from "../Layout";
import { Timeline } from "../Timeline";
import { NotFound } from "./NotFound";

/** Marks an English catalog key (collected by the i18n check); translated with t() where shown. */
const tr = (english: string) => english;

const STATUS = [
  { id: "current", label: tr("In the herd") },
  { id: "former", label: tr("Former") },
  { id: "reference", label: tr("Reference") },
];
/** The classes most ranches filter by; the rest are under More filters. */
const QUICK_CLASSES = ["cow", "heifer", "bull", "steer", "calf"] as const;
const SORTS = [{ id: "tag", label: tr("Tag") }, { id: "age", label: tr("Oldest first") }];
const PAGE_SIZE = 100;
const FILTER_KEYS = ["q", "status", "class", "place", "group", "sex", "sort"] as const;

export function AnimalRowView({ a, selected, onSelect, state }: { a: AnimalSummary; selected?: boolean; onSelect?: (v: boolean) => void; state?: unknown }) {
  return (
    <li className={cx("animal-row", selected && "row-selected", onSelect && "is-selecting")}>
      {onSelect && <Checkbox checked={!!selected} onChange={onSelect} label={t("Select {{animal}}", { animal: a.label })} />}
      <Link to={`/cattle/${a.id}`} state={state} className="animal-link">
        <span className="animal-id">{a.tag ? <TagLabel tag={a.tag} /> : <span className="animal-noname">{a.name ?? "—"}</span>}{a.tag && a.name && <span className="animal-name">{a.name}</span>}</span>
        <span className="animal-meta">
          <span>{classLabel(a.class)}</span>
          {a.age_days !== null && <span>{formatAge(a.age_days)}</span>}
          {a.breed && <span className="hide-narrow">{a.breed}</span>}
          {a.location && <span><Icon name="place" size={13} />{a.location.label}</span>}
          {a.status !== "active" && <span className="animal-status-word">{t(a.status === "reference" ? "Reference only" : a.status === "sold" ? "Sold" : a.status === "dead" ? "Dead" : a.status === "culled" ? "Culled" : a.status === "missing" ? "Missing" : "Transferred")}</span>}
        </span>
        <Badges badges={a.badges} max={3} />
      </Link>
    </li>
  );
}

export function CattleHome() {
  useLanguage();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "animals";
  return (
    <Page>
      <PageHeader title={t("Cattle")} actions={<Button icon="plus" onClick={() => openEntity({ kind: tab === "groups" ? "group" : "animal" })}>{tab === "groups" ? t("New group") : t("Add animal")}</Button>} />
      <Tabs value={tab} onChange={(id) => setParams(id === "animals" ? {} : { tab: id }, { replace: true })} tabs={[{ id: "animals", label: t("Animals") }, { id: "groups", label: t("Groups and lots") }]} />
      {tab === "groups" ? <GroupsList /> : <AnimalsList />}
    </Page>
  );
}

interface SavedView { name: string; query: string }

function AnimalsList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const can = useCan();
  const openComposer = useOpenComposer();
  const from = useFromHere(t("Cattle"));
  const q = params.get("q") ?? "";
  const status = params.get("status") ?? "current";
  const klass = params.get("class") ?? "";
  const place = params.get("place") ?? "";
  const group = params.get("group") ?? "";
  const sex = params.get("sex") ?? "";
  const sort = params.get("sort") ?? "tag";
  const [search, setSearch] = useState(q);
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [selecting, setSelecting] = useState(false);
  const [moreFilters, setMoreFilters] = useState(!!(place || group || sex || (klass && !QUICK_CLASSES.includes(klass as never))));
  const [views, setViews] = useLocalState<SavedView[]>("cattle.views", []);
  const [naming, setNaming] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const setParam = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  useEffect(() => setSearch(q), [q]);
  useEffect(() => { const h = setTimeout(() => { if (search !== q) setParam("q", search); }, 200); return () => clearTimeout(h); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  const filter = { q, status, class: klass, place, group, sex, sort };
  const list = useInfiniteQuery({
    queryKey: ["/animals", filter],
    queryFn: ({ pageParam }) => api.get<{ items: AnimalSummary[]; total: number }>(`/animals${qs({ ...filter, sort: sort === "tag" ? undefined : sort, limit: PAGE_SIZE, offset: pageParam || undefined })}`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => { const loaded = pages.reduce((n, p) => n + p.items.length, 0); return loaded < last.total ? loaded : undefined; },
    placeholderData: keepPreviousData,
  });
  const places = useApi<{ items: Array<{ id: string; name: string; head: number }> }>("/places");
  const groups = useApi<{ items: GroupSummary[] }>("/groups");
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total ?? 0;

  // Load the next page as the end of the list comes into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting) && list.hasNextPage && !list.isFetchingNextPage) void list.fetchNextPage(); }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage, items.length]);

  const toggle = (a: AnimalSummary, v: boolean) => setSelected((s) => { const n = new Map(s); if (v) n.set(a.id, a.label); else n.delete(a.id); return n; });
  const selectAll = async () => {
    if (items.length >= total) { setSelected(new Map(items.map((a) => [a.id, a.label]))); return; }
    const all = new Map<string, string>();
    for (let offset = 0; offset < Math.min(total, 5000); offset += 1000) {
      const page = await api.get<{ items: AnimalSummary[] }>(`/animals${qs({ ...filter, sort: undefined, limit: 1000, offset: offset || undefined })}`);
      for (const a of page.items) all.set(a.id, a.label);
    }
    setSelected(all);
  };
  const chosen = [...selected.entries()].map(([id, label]) => ({ id, label }));
  const animalTypes = eventTypesFor("animal").filter((e) => e.subjects.find((s) => s.kind === "animal")?.many);
  const activeFilters = [
    q && { key: "q", label: t("Matching “{{text}}”", { text: q }) },
    klass && { key: "class", label: classLabel(klass) },
    place && { key: "place", label: places.data?.items.find((p) => p.id === place)?.name ?? t("Place") },
    group && { key: "group", label: groups.data?.items.find((g) => g.id === group)?.name ?? t("Group") },
    sex && { key: "sex", label: t(sex === "female" ? "Female" : sex === "male" ? "Male" : "Unknown sex") },
  ].filter(Boolean) as Array<{ key: string; label: string }>;
  const viewQuery = new URLSearchParams(FILTER_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)!])).toString();
  const currentView = views.find((v) => v.query === viewQuery);

  return (
    <>
      <div className="list-tools">
        <div className="search-input"><Icon name="search" size={18} /><TextInput type="search" value={search} placeholder={t("Tag, name, EID, breed…")} aria-label={t("Search cattle")} onChange={(e) => setSearch(e.target.value)} /></div>
        <div className="filter-row">
          <div className="segmented" role="radiogroup" aria-label={t("Which cattle")}>
            {STATUS.map((s) => <button key={s.id} type="button" role="radio" aria-checked={status === s.id} className={cx("seg", status === s.id && "seg-active")} onClick={() => setParam("status", s.id === "current" ? "" : s.id)}>{t(s.label)}</button>)}
          </div>
        </div>
        <div className="filter-chips" role="group" aria-label={t("Class")}>
          <button type="button" className={cx("choice", !klass && "choice-on")} aria-pressed={!klass} onClick={() => setParam("class", "")}>{t("All classes")}</button>
          {QUICK_CLASSES.map((c) => <button key={c} type="button" className={cx("choice", klass === c && "choice-on")} aria-pressed={klass === c} onClick={() => setParam("class", klass === c ? "" : c)}>{classLabel(c)}</button>)}
          <button type="button" className={cx("choice", "choice-more", moreFilters && "is-open")} aria-expanded={moreFilters} onClick={() => setMoreFilters(!moreFilters)}><Icon name="filter" size={16} />{t("More filters")}</button>
        </div>
        {moreFilters && (
          <div className="filter-more">
            <Select aria-label={t("Class")} value={klass} onChange={(e) => setParam("class", e.target.value)}><option value="">{t("All classes")}</option>{Object.keys(ANIMAL_CLASS).map((c) => <option key={c} value={c}>{classLabel(c)}</option>)}</Select>
            <Select aria-label={t("Place")} value={place} onChange={(e) => setParam("place", e.target.value)}><option value="">{t("All places")}</option>{places.data?.items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
            <Select aria-label={t("Group")} value={group} onChange={(e) => setParam("group", e.target.value)}><option value="">{t("All groups")}</option>{groups.data?.items.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select>
            <Select aria-label={t("Sex")} value={sex} onChange={(e) => setParam("sex", e.target.value)}><option value="">{t("Any sex")}</option><option value="female">{t("Female")}</option><option value="male">{t("Male")}</option></Select>
          </div>
        )}
        {(views.length > 0 || activeFilters.length > 0) && (
          <div className="saved-views">
            {views.map((v) => (
              <span key={v.name} className={cx("view-chip", currentView?.name === v.name && "is-on")}>
                <button type="button" onClick={() => setParams(new URLSearchParams(v.query), { replace: true })} aria-pressed={currentView?.name === v.name}><Icon name="bookmark" size={14} />{v.name}</button>
                <button type="button" className="chip-x" aria-label={t("Forget the view {{name}}", { name: v.name })} onClick={() => { setViews(views.filter((x) => x.name !== v.name)); toast(t("View forgotten"), { action: { label: t("Undo"), run: () => setViews(views) } }); }}><Icon name="close" size={14} /></button>
              </span>
            ))}
            {activeFilters.length > 0 && !currentView && (naming === null
              ? <Button size="sm" variant="ghost" icon="bookmark" onClick={() => setNaming("")}>{t("Save this view")}</Button>
              : <form className="view-name" onSubmit={(e) => { e.preventDefault(); const name = naming.trim(); if (!name) return; setViews([...views.filter((v) => v.name !== name), { name, query: viewQuery }]); setNaming(null); toast(t("Saved on this device"), { tone: "good" }); }}>
                  <TextInput value={naming} onChange={(e) => setNaming(e.target.value)} placeholder={t("Name it: Open cows")} aria-label={t("View name")} autoFocus />
                  <Button size="sm" type="submit" variant="primary">{t("Save")}</Button>
                  <IconButton icon="close" label={t("Cancel")} onClick={() => setNaming(null)} />
                </form>)}
          </div>
        )}
      </div>
      <div className="list-summary">
        <span aria-live="polite">{list.data ? plural(total, "{{count}} animal", "{{count}} animals") : ""}</span>
        {activeFilters.map((f) => <Chip key={f.key} tone="neutral" onRemove={() => setParam(f.key, "")} removeLabel={t("Remove filter: {{filter}}", { filter: f.label })}>{f.label}</Chip>)}
        {activeFilters.length > 1 && <button type="button" className="link small" onClick={() => setParams(status !== "current" ? { status } : {}, { replace: true })}>{t("Clear filters")}</button>}
        <span className="list-summary-end">
          <label className="sort"><Icon name="sort" size={16} /><span className="sr-only">{t("Sort")}</span>
            <Select aria-label={t("Sort")} value={sort} onChange={(e) => setParam("sort", e.target.value === "tag" ? "" : e.target.value)}>{SORTS.map((s) => <option key={s.id} value={s.id}>{t(s.label)}</option>)}</Select>
          </label>
          {can("record") && <Button size="sm" variant="ghost" icon={selecting ? "close" : "check"} onClick={() => { setSelecting(!selecting); setSelected(new Map()); }}>{selecting ? t("Done") : t("Select")}</Button>}
        </span>
      </div>
      {selecting && (
        <div className="select-row">
          <Button size="sm" variant="ghost" onClick={() => void selectAll()}>{total > items.length ? t("Select all {{count}}", { count: total }) : t("Select all")}</Button>
          {selected.size > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}>{t("Clear selection")}</Button>}
        </div>
      )}
      {list.isLoading ? <Loading /> : list.error && !list.data ? <ErrorNote error={list.error} retry={() => list.refetch()} /> : items.length === 0 ? (
        <Empty icon="cattle" title={q || activeFilters.length ? t("No cattle match") : status !== "current" ? t("None here") : t("No cattle here yet")}
          body={q || activeFilters.length ? t("Try a different tag or clear the filters.") : status !== "current" ? t("Sold, dead and reference animals show here when there are some.") : t("Add them one at a time, bring in a spreadsheet, or just say: “Add cow 42, red, born 2019.”")}
          action={activeFilters.length ? <Button onClick={() => setParams({}, { replace: true })}>{t("Clear filters")}</Button> : !q && status === "current" && can("record") && <Button variant="primary" icon="mic" onClick={() => openComposer({ draft: t("Add cow ") })}>{t("Tell Hondo")}</Button>} />
      ) : (
        <>
          <ul className={cx("animal-list", list.isPlaceholderData && "is-stale")} aria-busy={list.isFetching}>
            {items.map((a) => <AnimalRowView key={a.id} a={a} state={from} selected={selected.has(a.id)} onSelect={selecting ? (v) => toggle(a, v) : undefined} />)}
          </ul>
          <div ref={sentinel} className="list-end">
            {list.hasNextPage ? <Button variant="ghost" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>{t("Show more ({{shown}} of {{total}})", { shown: items.length, total })}</Button>
              : items.length > 20 && <span className="muted small">{t("That's all {{count}}.", { count: total })}</span>}
          </div>
        </>
      )}
      {chosen.length > 0 && (
        <div className="bulk-bar" role="region" aria-label={t("Selected cattle")}>
          <span className="bulk-count">{plural(chosen.length, "{{count}} selected", "{{count}} selected")}</span>
          <Button variant="primary" icon="mic" onClick={() => openComposer({ draft: t("For {{tags}}: ", { tags: chosen.slice(0, 12).map((a) => a.label).join(", ") + (chosen.length > 12 ? "…" : "") }) })}>{t("Tell Hondo")}</Button>
          {animalTypes.length > 0 && <Menu label={t("Record for these")} variant="secondary" items={animalTypes.map((e) => ({
            label: t(e.label), icon: e.icon,
            onSelect: () => openRecord({ initial: { type: e.type, subjects: chosen.map((a) => ({ kind: "animal" as const, id: a.id, role: e.subjects.find((s) => s.kind === "animal")!.role, label: a.label })) } }),
          }))}>{t("Record")}</Menu>}
          <Button variant="ghost" icon="work" onClick={() => navigate(`/work?start=1&animals=${chosen.map((a) => a.id).join(",")}`)}>{t("Start a job")}</Button>
          <Button variant="ghost" icon="report" onClick={() => navigate(cattleReportLink({ animals: chosen.map((a) => a.id) }))}>{t("Report on these")}</Button>
          <IconButton icon="close" label={t("Clear selection")} onClick={() => setSelected(new Map())} />
        </div>
      )}
    </>
  );
}

function GroupsList() {
  const groups = useApi<{ items: GroupSummary[] }>("/groups");
  const from = useFromHere(t("Groups and lots"));
  if (groups.isLoading) return <Loading />;
  if (groups.error && !groups.data) return <ErrorNote error={groups.error} retry={() => groups.refetch()} />;
  if (!groups.data?.items.length) return <Empty icon="group" title={t("No groups yet")} body={t("Groups keep a herd, a pen or a sale lot together. Membership history is kept.")} action={<Button variant="primary" icon="plus" onClick={() => openEntity({ kind: "group" })}>{t("New group")}</Button>} />;
  return (
    <List>
      {groups.data.items.map((g) => (
        <Row key={g.id} icon={g.kind === "lot" ? "boxes" : "group"} to={`/cattle/groups/${g.id}`} state={from} title={g.name}
          meta={[t(g.kind === "saved_view" ? "Saved list" : g.kind === "lot" ? "Lot" : g.kind === "management" ? "Management group" : g.kind === "marketing" ? "Marketing group" : g.kind === "contemporary" ? "Contemporary group" : "Herd"),
            g.counted_head !== null ? t("{{count}} counted", { count: g.counted_head }) : plural(g.head, "{{count}} head", "{{count}} head"), g.place?.label].filter(Boolean).join(" · ")} />
      ))}
    </List>
  );
}

export function GroupPage() {
  const { id } = useParams();
  const g = useApi<{ id: string; name: string; kind: string; counted_head: number | null; place: { id: string; label: string } | null; members: AnimalSummary[]; former: Array<{ animal_id: string; label: string; joined_at: string; left_at: string }> }>(`/groups/${id}`);
  useCaptureSubject(g.data ? { subject: { kind: "group", id: g.data.id, label: g.data.name } } : null);
  const [picked, setTab] = useState<string | null>(null);
  const sections = entitySections("group").filter((s) => !g.data || !s.show || s.show(g.data));
  const tab = picked ?? sections[0]?.id ?? "animals";
  const from = useFromHere(g.data?.name ?? t("Group"));
  if (g.isLoading) return <Loading />;
  if (!g.data) return (g.error as { status?: number } | null)?.status === 404 ? <NotFound /> : <Page><ErrorNote error={g.error} retry={() => g.refetch()} /></Page>;
  return (
    <Page>
      <PageHeader back={{ to: "/cattle?tab=groups", label: t("Groups and lots") }} title={g.data.name}
        subtitle={[plural(g.data.members.length, "{{count}} head", "{{count}} head"), g.data.place?.label].filter(Boolean).join(" · ")}
        actions={<Button icon="edit" onClick={() => openEntity({ kind: "group", id: g.data!.id, initial: { name: g.data!.name } })}>{t("Edit")}</Button>} />
      <Tabs value={tab} onChange={setTab} tabs={[...sections.map((s) => ({ id: s.id, label: t(s.label) })), { id: "animals", label: t("Animals"), count: g.data.members.length }, { id: "history", label: t("History") }, { id: "former", label: t("Former members"), count: g.data.former.length }]} />
      {sections.filter((s) => s.id === tab).map((s) => <Suspense key={s.id} fallback={<Spinner />}><s.Component entity={g.data!} /></Suspense>)}
      {tab === "animals" && (g.data.members.length ? <ul className="animal-list">{g.data.members.map((a) => <AnimalRowView key={a.id} a={a} state={from} />)}</ul> : <p className="muted">{t("No animals in this group right now.")}</p>)}
      {tab === "history" && <Timeline subject={`group:${g.data.id}`} />}
      {tab === "former" && <List>{g.data.former.map((f) => <Row key={`${f.animal_id}${f.left_at}`} icon="animal" to={`/cattle/${f.animal_id}`} state={from} title={f.label} meta={t("{{from}} to {{to}}", { from: f.joined_at, to: f.left_at })} />)}</List>}
    </Page>
  );
}

export { Card, Chip, Section };
